import * as yup from 'yup'

// Canonical staff positions offered in the "Cargo" selector, in display order.
// "Otro" (last) reveals a free-text input for positions outside the list.
export const CARGO_OPTIONS = [
  'Seguridad', 'Limpieza', 'Administración', 'Asistencia de administración', 'Otro'
]

// Normalizes a position label for tolerant matching of legacy/uppercased data:
// uppercases, strips accents, drops the standalone connector "DE" and collapses
// whitespace so "ASISTENCIA ADMINISTRACION" matches "Asistencia de administraci\u00f3n".
function normalizePosition (value) {
  return (value || '')
    .toString()
    .toUpperCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\bDE\b/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

// Resolves the stored position value from the form state: the free-text field
// when "Otro" is selected, otherwise the selected canonical option.
export function resolvePosition (form) {
  return form.positionSelect === 'Otro'
    ? (form.positionOther || '').trim()
    : (form.positionSelect || '')
}

// Given a raw stored position (possibly uppercased/unnormalized), returns the
// { positionSelect, positionOther } form state. Matches case-insensitively and
// accent-insensitively against the canonical options; anything else → "Otro".
export function positionToForm (rawPosition) {
  const raw = (rawPosition || '').toString().trim()
  if (!raw) return { positionSelect: '', positionOther: '' }
  const normRaw = normalizePosition(raw)
  const match = CARGO_OPTIONS.find(
    opt => opt !== 'Otro' && normalizePosition(opt) === normRaw
  )
  if (match) return { positionSelect: match, positionOther: '' }
  return { positionSelect: 'Otro', positionOther: raw }
}

// Banks offered in the payment-method selector, in display order.
// "Otro" (last) reveals a free-text input for banks outside the list.
export const BANK_OPTIONS = [
  'BCP', 'BBVA', 'Interbank', 'Scotiabank', 'BanBif', 'Banco de la Nación',
  'Banco Pichincha', 'Mibanco', 'Caja Arequipa', 'Caja Huancayo', 'Caja Piura',
  'Caja Trujillo', 'Caja Cusco', 'Caja Sullana', 'Otro'
]

export const PAYMENT_METHODS = ['savings', 'checking', 'yape', 'plin']

// Optional payment fields; only validated when actually filled in.
export const paymentSchema = {
  method: yup.string().oneOf(PAYMENT_METHODS, 'Forma de pago inválida').nullable().label('Forma de pago'),
  bank: yup.string().max(40).label('Banco'),
  accountNumber: yup.string().matches(/^\d+$/, { message: 'Solo dígitos', excludeEmptyString: true }).max(20).label('N° de cuenta'),
  cci: yup.string().matches(/^\d{20}$/, { message: 'CCI debe tener 20 dígitos', excludeEmptyString: true }).label('CCI'),
  phone: yup.string().matches(/^\d{9}$/, { message: 'Celular debe tener 9 dígitos', excludeEmptyString: true }).label('Celular')
}

export function isAccountMethod (method) {
  return method === 'savings' || method === 'checking'
}

export function isWalletMethod (method) {
  return method === 'yape' || method === 'plin'
}

// Builds the staff.payment object with only the fields relevant to the chosen
// method. Returns null when no method is selected (so we store payment: null).
export function buildPayment (form) {
  const method = form.paymentMethod || ''
  if (!PAYMENT_METHODS.includes(method)) return null
  if (isAccountMethod(method)) {
    const bank = form.bankSelect === 'Otro' ? (form.bankOther || '').trim() : (form.bankSelect || '')
    const payment = { method }
    if (bank) payment.bank = bank
    const account = (form.accountNumber || '').trim()
    if (account) payment.accountNumber = account
    const cci = (form.cci || '').trim()
    if (cci) payment.cci = cci
    return payment
  }
  const phone = (form.phone || '').trim()
  const payment = { method }
  if (phone) payment.phone = phone
  return payment
}

// Maps a form's payment fields to the Yup schema paths, keyed by method, so
// validation errors land on errors.bank / errors.accountNumber / etc.
export function paymentValues (form) {
  const method = form.paymentMethod || null
  if (isAccountMethod(method)) {
    const bank = form.bankSelect === 'Otro' ? form.bankOther : form.bankSelect
    return { method, bank: bank || '', accountNumber: form.accountNumber || '', cci: form.cci || '' }
  }
  if (isWalletMethod(method)) {
    return { method, phone: form.phone || '' }
  }
  return { method }
}
